import { ref } from 'vue';
import type { Ref } from 'vue';
import { hasDuplicates } from '@/csv/utils/csv-file-parser';
import logger from '@/logger';

const log = logger.getChild('base-field-mapper-util');

export interface BaseField<TDialpadType> {
  original: string;
  example: string;
  dialpad: TDialpadType | string | null;
}

export type BaseFieldMap<TDialpadType> = Record<
  string,
  TDialpadType | undefined | null
>;
export type BaseCustomFieldMap = Record<string, string>;

export abstract class BaseFieldMapperUtil<TDialpadType> {
  abstract readonly requiredFields: TDialpadType[];
  abstract readonly fieldsValuesSet: Set<string>;

  fields: Ref<BaseField<TDialpadType>[]> = ref([]);
  map: Ref<BaseFieldMap<TDialpadType>> = ref({});
  customMap: Ref<BaseCustomFieldMap> = ref({});
  csvFields: Ref<string[]> = ref([]);

  protected abstract getLoggerName(): string;

  protected isValidDialpadType(
    value: TDialpadType | string | null,
  ): value is TDialpadType {
    return value !== null && this.fieldsValuesSet.has(value as string);
  }

  reset(): void {
    const logger = log.getChild(this.getLoggerName());
    logger.debug('Resetting');
    this.fields.value = [];
    this.updateMap();
  }

  init(
    maps: {
      map: BaseFieldMap<TDialpadType> | undefined;
      customMap: BaseCustomFieldMap | undefined;
    },
    fields: string[],
    examples: string[],
  ): boolean {
    const logger = log.getChild(this.getLoggerName());
    logger.debug('Initializing', { maps, fields, examples });
    this.reset();

    if (fields.length !== examples.length) return false;
    if (hasDuplicates(fields)) return false;
    if (fields.some((field) => !field)) return false;
    if (fields.length < this.requiredFields.length) return false;

    if (this.validateMap(maps, fields)) {
      this.fillWithMap(maps, fields, examples);
    } else {
      this.fillWithoutMap(fields, examples);
    }

    this.csvFields.value = fields;
    this.updateMap();

    return true;
  }

  private validateMap(
    {
      map,
      customMap,
    }: {
      map: BaseFieldMap<TDialpadType> | undefined;
      customMap: BaseCustomFieldMap | undefined;
    },
    fields: string[],
  ): boolean {
    const logger = log.getChild(this.getLoggerName());
    logger.debug('Validating map', { map, customMap, fields });

    const allKeys = [
      ...Object.keys(map ?? {}),
      ...Object.keys(customMap ?? {}),
    ];
    if (!this.areKeysInFields(allKeys, fields, logger)) {
      return false;
    }

    if (!this.areRequiredFieldsMapped(map, logger)) {
      return false;
    }

    logger.debug('Map is valid');
    return true;
  }

  private areKeysInFields(
    keys: string[],
    fields: string[],
    logger: ReturnType<typeof log.getChild>,
  ): boolean {
    for (const key of keys) {
      if (!fields.includes(key)) {
        logger.debug(`Map key ${key} not found in fields`);
        return false;
      }
    }
    return true;
  }

  private areRequiredFieldsMapped(
    map: BaseFieldMap<TDialpadType> | undefined,
    logger: ReturnType<typeof log.getChild>,
  ): boolean {
    const mappedValues = new Set(
      Object.values(map ?? {}).filter(
        (v): v is TDialpadType => v !== null && v !== undefined,
      ),
    );
    for (const requiredField of this.requiredFields) {
      if (!mappedValues.has(requiredField)) {
        logger.debug(`Required field ${requiredField} not found in map`);
        return false;
      }
    }
    return true;
  }

  private fillWithMap(
    {
      map,
      customMap,
    }: {
      map: BaseFieldMap<TDialpadType> | undefined;
      customMap: BaseCustomFieldMap | undefined;
    },
    fields: string[],
    examples: string[],
  ) {
    const logger = log.getChild(this.getLoggerName());
    logger.debug('Filling with map', { map, fields, examples });

    for (const requiredField of this.requiredFields) {
      for (const [key, value] of Object.entries(map ?? {})) {
        if (value === requiredField) {
          this.fields.value.push({
            original: key,
            example: examples[fields.indexOf(key)],
            dialpad: requiredField,
          });
          break;
        }
      }
    }

    for (let i = 0; i < fields.length; i++) {
      if (this.fields.value.some((field) => field.original === fields[i]))
        continue;

      const dialpad = map?.[fields[i]];
      const customDialpad = customMap?.[fields[i]];

      if (dialpad !== undefined) {
        this.fields.value.push({
          original: fields[i],
          example: examples[i],
          dialpad,
        });
      } else if (customDialpad) {
        this.fields.value.push({
          original: fields[i],
          example: examples[i],
          dialpad: customDialpad,
        });
      } else {
        this.fields.value.push({
          original: fields[i],
          example: examples[i],
          dialpad: null,
        });
      }
    }
  }

  protected abstract fillWithoutMap(fields: string[], examples: string[]): void;

  protected updateMap(): void {
    const logger = log.getChild(this.getLoggerName());
    logger.debug('Updating map');

    const newMap: BaseFieldMap<TDialpadType> = {};
    const newCustomMap: BaseCustomFieldMap = {};

    for (const field of this.fields.value) {
      if (this.isValidDialpadType(field.dialpad)) {
        newMap[field.original] = field.dialpad;
      } else if (field.dialpad) {
        newCustomMap[field.original] = field.dialpad;
      } else {
        newMap[field.original] = null;
      }
    }

    this.map.value = newMap;
    this.customMap.value = newCustomMap;
  }

  validate(): boolean {
    const logger = log.getChild(this.getLoggerName());
    logger.debug('Validating fields');

    if (this.fields.value.length === 0) return false;

    const mappedFields = new Set(
      this.fields.value
        .filter((field) => field.dialpad !== null)
        .map((field) => field.dialpad),
    );

    for (const requiredField of this.requiredFields) {
      if (!mappedFields.has(requiredField)) {
        logger.debug(`Required field ${requiredField} not mapped`);
        return false;
      }
    }

    logger.debug('Valid');
    return true;
  }

  changeOriginal(index: number, newValue: string): void {
    const logger = log.getChild(this.getLoggerName());
    logger.debug('Changing original', { index, newValue });

    if (index >= this.fields.value.length)
      throw new Error('Index is out of the fields array');

    let targetIndex = -1;
    for (let i = 0; i < this.fields.value.length; i++) {
      const field = this.fields.value[i];
      if (field.original === newValue) {
        targetIndex = i;
        break;
      }
    }

    if (targetIndex === -1)
      throw new Error(`${newValue} does not exist in fields`);

    [
      this.fields.value[index].original,
      this.fields.value[targetIndex].original,
      this.fields.value[index].example,
      this.fields.value[targetIndex].example,
    ] = [
      this.fields.value[targetIndex].original,
      this.fields.value[index].original,
      this.fields.value[targetIndex].example,
      this.fields.value[index].example,
    ];

    this.handleFieldSwap(index, targetIndex);
    this.updateMap();
  }

  protected handleFieldSwap(index: number, targetIndex: number): void {
    if (targetIndex >= this.requiredFields.length) {
      this.fields.value[targetIndex].dialpad = null;
    }
  }

  changeDialpad(index: number, newValue: TDialpadType | null | string): void {
    const logger = log.getChild(this.getLoggerName());
    logger.debug('Changing dialpad', { index, newValue });
    this.validateIndex(index);

    this.performCustomValidation(index, newValue);
    this.clearExistingMapping(newValue, this.getDoNotImportValue());

    this.fields.value[index].dialpad = newValue;
    this.updateMap();
  }

  protected abstract performCustomValidation(
    index: number,
    newValue: TDialpadType | null | string,
  ): void;

  protected abstract getDoNotImportValue(): TDialpadType;

  protected validateIndex(index: number): void {
    if (index > this.fields.value.length) {
      throw new Error('Index is out of the fields array');
    }
  }

  protected clearExistingMapping(
    newValue: TDialpadType | null | string,
    doNotImportValue: TDialpadType,
  ): void {
    if (newValue !== doNotImportValue && newValue !== null) {
      for (const field of this.fields.value) {
        if (field.dialpad === newValue) {
          field.dialpad = null;
        }
      }
    }
  }
}
